import {
  Alignment,
  MobileAlignment,
  BackgroundColor,
  EnterpriseBackgroundColor,
  CalloutColumns,
  Colorways,
  Margins,
  LinkTargets,
} from 'types/constants';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Color } from 'libs/contentful/props';
import { getColorwayName } from 'utils/formatters';
import { PageContext } from 'utils/pageContext';
import { isEnterprisePage } from 'utils/enterprise';
import { Themes } from 'types/constants';
import { isReferralPage } from 'utils/referralHelpers';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getColorway = (colorway: { name: Colorways } | Colorways): BackgroundColor | EnterpriseBackgroundColor => {
  const color = getColorwayName(colorway);

  switch (color) {
    case Colorways.Secondary:
      return BackgroundColor.LightGreen;

    case Colorways.Tertiary:
      return BackgroundColor.MediumGreen;

    // The Inverse colorway is overridden to use Flush background on enterprise pages
    // See src/app/globals.css
    case Colorways.Inverse:
      return BackgroundColor.DarkGreen;

    case Colorways.DarkestGreen:
      return BackgroundColor.DarkestGreen;

    case Colorways.DarkBlue:
      return BackgroundColor.DarkBlue;

    case Colorways.ActionPrimary:
      return BackgroundColor.BrightGreen;

    case Colorways.Yellow:
      return BackgroundColor.Yellow;

    case Colorways.AccentBlue100:
      return BackgroundColor.AccentBlue100;
    case Colorways.AccentBlue200:
      return BackgroundColor.AccentBlue200;
    case Colorways.AccentYellow200:
      return BackgroundColor.AccentYellow200;
    case Colorways.AccentYellow300:
      return BackgroundColor.AccentYellow300;
    case Colorways.AccentRed300:
      return BackgroundColor.AccentRed300;
    case Colorways.AccentRed500:
      return BackgroundColor.AccentRed500;
    case Colorways.AccentPink200:
      return BackgroundColor.AccentPink200;
    case Colorways.AccentPink600:
      return BackgroundColor.AccentPink600;

    case Colorways.DarkGreenGradient:
      return BackgroundColor.DarkGreenGradient;

    case Colorways.DarkGreenGradientLeftToRight:
      return BackgroundColor.DarkGreenGradientLeftToRight;

    default:
    case Colorways.Primary:
      return BackgroundColor.White;
  }
};

export const getColorwayWithTransparency = (
  colorway: { name: Colorways } | Colorways,
  transparentBackground?: boolean,
) => {
  return cn(transparentBackground ? 'bg-transparent bg-none color-inherit' : getColorway(colorway));
};

export const getCustomBackgroundColor = (colorway: Color) => {
  if (colorway.name !== Colorways.Custom) {
    return;
  }

  return { backgroundColor: colorway.value };
};

export const getYMargins = (margins: Margins) => {
  // 80px desktop
  // 48px mobile

  // Note: generally these are the top and bottom margins for each component
  // Exceptions being Hero, Disclosure, and Copy Add on With Button

  const marginStyles: { [key in Margins]: string } = {
    [Margins.NoTop]: cn('pt-0', 'pb-12 md:pb-20'),
    [Margins.NoBottom]: cn('pt-12 md:pt-20', 'pb-0'),
    [Margins.NoMargins]: cn('p-0', 'm-0'),
    [Margins.Centered]: cn('py-12 md:py-20'),
  };

  return marginStyles[margins];
};

export const getYMarginsSlim = (margins: Margins) => {
  // 48px desktop
  // 24px mobile

  // Note: generally these are the top and bottom margins for each component
  // Exceptions being Hero, Disclosure, and Copy Add on With Button

  const marginStyles: { [key in Margins]: string } = {
    [Margins.NoTop]: cn('pt-0', 'pb-6 md:pb-12'),
    [Margins.NoBottom]: cn('pt-6 md:pt-12', 'pb-0'),
    [Margins.NoMargins]: cn('p-0', 'm-0'),
    [Margins.Centered]: cn('py-6 md:py-12'),
  };

  return marginStyles[margins];
};

export const getAlignment = (alignment: Alignment, mobileAlignment?: MobileAlignment) => {
  return cn(
    'sm:flex-row',
    alignment === Alignment.Right && 'sm:flex-row-reverse',
    mobileAlignment === MobileAlignment.MediaBelowCopy && 'max-sm:flex-col-reverse',
  );
};

export const getCalloutColumns = (column: CalloutColumns) => {
  switch (column) {
    case CalloutColumns.Two:
      return 'basis-1/2-gap-6';
    case CalloutColumns.Four:
      return 'basis-1/4-gap-6';
    case CalloutColumns.Five:
      return 'basis-1/2-gap-6 md:basis-1/5-gap-6 md:grow';
    default:
    case CalloutColumns.Three:
      return 'basis-1/3-gap-6';
  }
};

export const getLinkTarget = (target: LinkTargets) => {
  const linkTargets: { [key in LinkTargets]: string } = {
    [LinkTargets.NewTab]: '_blank',
    [LinkTargets.SameTab]: '_self',
  };

  return linkTargets[target];
};

export const getTheme = (headers: PageContext): Themes => {
  if (isEnterprisePage(headers)) {
    return Themes.Enterprise;
  }

  if (isReferralPage(headers.path)) return Themes.Referral;

  return Themes.Www;
};

export const getSectionProps = (
  color?: { name: Colorways } | Colorways,
  margins?: Margins,
  sectionName?: string | null,
  addtlClasses?: string,
) => {
  return {
    ...(sectionName != null && { id: encodeURIComponent(sectionName) }),
    className: cn(
      color && getColorway(color),
      margins && getYMargins(margins),
      sectionName && 'scroll-mt-[var(--height-nav)]',
      addtlClasses,
    ),
  };
};

export const calculateOverlayMargin = (isOverlayShown: boolean | undefined): string => {
  return isOverlayShown ? 'mt-20 sm:mt-6' : '';
};
